import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Componentes UI
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
// Componentes
import TableLayout from '../../layouts/layout-tabla';
import EstaSeguro from '../../components/alertas/esta-seguro';
import Permiso from '../../components/wrappers/permiso';
// Datos
import revisionService from '../../services/revisionService';
import { useCatalogosMecanica } from '../../hooks/cat-recepcion-tm';
// Utils
import { exportarPDF } from '../../utils/pdf/pdfDataTable';
import { exportarExcel } from '../../utils/exportarExcel';
import { inicioDelDia, finDelDia, formatFecha } from '../../utils/dateFormatter';
import {columnasPDF, columnasExcel } from '../../utils/pdf/columnas/seguimientoTaller';
// Assets
import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import './servicios-tallermec.css';


const OPCIONES_ESTADO = [
  { label: 'Todos', value: null },
  { label: 'En taller', value: 'ingresado' },
  { label: 'Entregados', value: 'entregado' },
];

function BadgeEstadoRecepcion({ estado }) {
  if (estado === 'entregado') {
    return (<div className="badge-estado">
      <Tag severity="success" value="Entregado" icon="pi pi-check-circle" className='tag-estado'/>
    </div>);
  }
  return (
    <div style={{ textAlign: 'center' }}>
    <Tag severity="info" value="Ingresado" icon="pi pi-car" className='tag-estado'/>
    </div>);
}

function BadgeEstadoPaso({ estado }) {
  if (estado === 'completo') {
    return (<div className="badge-estado">
      <Tag severity="success" value="Completo" icon="pi pi-check-circle" className='tag-estado'/>
    </div>);
  }
  return (<div style={{ textAlign: 'center' }}>
    <Tag severity="warning" value="Pendiente" icon="pi pi-clock" className='tag-estado'/>
  </div>);
}

function diasDesde(fecha) {
  if (!fecha) return null;
  return Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24));
}

function clienteTemplate(rowData) {
  return (
    <div style={{ textAlign: 'center' }}>
      <strong>{rowData.nombre_cliente}</strong>
      <><br />
        <small className="text-muted">
          +56 {rowData.celular_cliente}
        </small>
      </>
    </div>
  );
};

function vehiculoTemplate(rowData) {
  return (
    <div style={{ textAlign: 'center' }}>
      <strong>{rowData.patente}</strong>
      <><br />
        <small className="text-muted">
          {rowData.marca.toUpperCase()} {rowData.modelo.toUpperCase()}
        </small>
      </>
    </div>
  );
};

function mecanicosTemplate(rowData) {
  const mecanicos = rowData.trabajadores?.filter(
    (trabajador) =>
      trabajador.tipo?.toLowerCase() === 'mecanico'
  ) ?? [];

  if (mecanicos.length === 0) {
    return (
      <span className="text-muted">
        Sin asignar
      </span>
    );
  }

  const MAX_VISIBLES = 2;
  const visibles = mecanicos.slice(0, MAX_VISIBLES);
  const restantes = mecanicos.length - visibles.length;

  return (
    <div style={{ textAlign: 'center' }}>
      {visibles.map((mecanico) => (
        <div key={mecanico.id_trabajador}>
          {mecanico.nombre}
        </div>
      ))}
      {restantes > 0 && (
        <div className="text-muted">
          +{restantes}
        </div>
      )}
    </div>
  );
}

function nombreRecepcionista(rowData) {
  const recepciona = rowData.trabajadores?.find(
    (trabajador) => trabajador.tipo?.toLowerCase() === 'tecnico');
  return recepciona?.nombre ?? '';
}

function recepcionistaTemplate(rowData) {
  return (
    <div style={{ textAlign: 'center' }}>
      {nombreRecepcionista(rowData) ?? '—'}
    </div>
  );
}

function SeguimientoRevisiones({ mecanicoActualId = null }) {
  const navigate = useNavigate();
  const toast = useRef(null);
  const dtRef = useRef(null);
  const [revisiones, setRevisiones] = useState([]);
  const [cargando, setCargando] = useState(true);

  // Vienen del mismo caché de módulo que usa el paso 1.
  const { centros, mecanicos } = useCatalogosMecanica();

  const [filtroCentro, setFiltroCentro] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState(null);
  const [filtroMecanico, setFiltroMecanico] = useState(mecanicoActualId ?? null);
  const [periodo, setPeriodo] = useState(null); // [desde, hasta] del Calendar range

  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });

  const CAMPOS_BUSQUEDA = ['patente', 'nombre_cliente', 'nombre', 'modelo', 'nombre_recepcionista'];

  const [mostrarConfirmacionEntrega, setMostrarConfirmacionEntrega] = useState(false);
  const [revisionSeleccionada, setRevisionSeleccionada] =  useState(null);
  const [entregandoVehiculo, setEntregandoVehiculo] = useState(false);

  const cargarRevisiones = useCallback(async () => {
    // El spinner solo se muestra si la carga tarda más de 200ms.
    const mostrarSpinner = setTimeout(() => setCargando(true), 200);

    try {
      const [desde, hasta] = periodo ?? [null, null];

      const data = await revisionService.listarRevisiones({
        id_centrocosto: filtroCentro,
        estado: filtroEstado,
        desde: inicioDelDia(desde),
        hasta: finDelDia(hasta),
        id_trabajador: filtroMecanico,
        limite: 100,
      });

      const nuevaData = data.map((fila) => ({
        ...fila, 
        nombre_recepcionista: nombreRecepcionista(fila),
        dias_pendiente: fila.estado_recepcion === 'entregado' ? null : diasDesde(fila.fecha_ingreso),
      }));

      setRevisiones(nuevaData);
    } catch (err) {
      console.error('Error cargando revisiones:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error al cargar',
        detail: 'No se pudieron cargar las revisiones.',
        life: 4000,
      });
    } finally {
      clearTimeout(mostrarSpinner);
      setCargando(false);
    }
  }, [filtroCentro, filtroEstado, periodo, filtroMecanico ]);

  useEffect(() => { cargarRevisiones(); }, [cargarRevisiones]);

  const irARevision = (revision) => {
    let paso = 0;
    if (revision.estado_taller !== 'completo') paso = 1;
    else if (revision.estado_firmas !== 'completo') paso = 2;
    navigate(`/app/taller/revisiones/${revision.id_revision}?paso=${paso}`);
  };

  const diasTemplate = (row) => {
    const dias = diasDesde(row.fecha_ingreso);

    if (dias === null) return '—';

    if (row.estado_recepcion === 'entregado') {
      return <span className="text-muted">Entregado</span>;
    }

    return (
      <div style={{ textAlign: 'center' }}>
        <span className={dias >= 3 ? 'text-danger fw-bold' : ''}>
          {dias === 0 ? 'Hoy' : `${dias} día${dias === 1 ? '' : 's'}`}
        </span>
      </div>
    );
  };

  const filasParaExportar = (() => {
    const termino = globalFilterValue.trim().toLowerCase();
    if (!termino) return revisiones;

    return revisiones.filter((fila) =>
      CAMPOS_BUSQUEDA.some((campo) =>
        String(fila[campo] ?? '').toLowerCase().includes(termino)
      )
    );
  })();

  const subtituloPeriodo = () => {
    if (!periodo || !periodo[0]) return null;
    const [desde, hasta] = periodo;
    const desdeTxt = desde.toLocaleDateString('es-CL');
    const hastaTxt = hasta ? hasta.toLocaleDateString('es-CL') : '...';
    return `Período: ${desdeTxt} — ${hastaTxt}`;
  };

  const manejarExportarExcel = () => {
    exportarExcel({
      rows: filasParaExportar,
      columns: columnasExcel,
      filename: `seguimiento-taller-${new Date().toISOString().slice(0, 10)}.xlsx`,
    });
  };

  const manejarExportarPdf = () => {
    exportarPDF({
      rows: filasParaExportar,
      columns: columnasPDF,
      nombreArchivo: `seguimiento-taller-${new Date().toISOString().slice(0, 10)}.pdf`,
      tituloArchivo: 'SEGUIMIENTO DE INFORMES TALLER',
      subTituloArchivo: subtituloPeriodo(),
      idEmpresa: 2,
    });
  };

  const abrirConfirmacionEntrega = (revision) => {
    setRevisionSeleccionada(revision);
    setMostrarConfirmacionEntrega(true);
  };

  const confirmarEntregaVehiculo = async () => {
    if (!revisionSeleccionada) return;

    try {
      setEntregandoVehiculo(true);

      await revisionService.entregarVehiculo(
        revisionSeleccionada.id_revision
      );

      setMostrarConfirmacionEntrega(false);
      setRevisionSeleccionada(null);

      await cargarRevisiones();

      toast.current?.show({
        severity: 'success',
        summary: 'Vehículo entregado',
        detail: 'El informe fue bloqueado y se generó el PDF oficial.',
        life: 5000,
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error al entregar',
        detail: error?.message || 'No se pudo completar la entrega.',
        life: 5000,
      });
    } finally {
      setEntregandoVehiculo(false);
    }
  };

  // ---------------- Toolbar ----------------
  const startToolbar = () => (
    <div className="tmc-toolbar-group">
      {/* Botón Todos */}
      <Button
        label="TODOS"
        className={ filtroCentro === null ? 'btn-empresa-activo' : 'btn-empresa' }
        onClick={() => {
          setFiltroCentro(null);
          setGlobalFilterValue('');
          setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS }});
        }}
      />

      {/* Filtro por Centro de costo */}
      {centros
        .filter((centro) => centro.nombre !== 'NO ASIGNADO')
        .map((centro) => (
        <Button
          key={centro.id_centrocosto ?? 'todos'}
          label={centro.nombre}
          className={ filtroCentro === centro.id_centrocosto ? 'btn-empresa-activo' : 'btn-empresa' }
          onClick={() => {
            setFiltroCentro(centro.id_centrocosto);
            setGlobalFilterValue('');
            setFilters({ global: { value: null, matchMode: FilterMatchMode.CONTAINS }});
          }}
        />
      ))}

      <Button
        icon="pi pi-file-excel"
        className="btn-outline-verde"
        tooltip="Exportar a Excel"
        tooltipOptions={{ position: 'top' }}
        disabled={filasParaExportar.length === 0}
        onClick={manejarExportarExcel}
      />
      <Button
        icon="pi pi-file-pdf"
        className="btn-outline-rojo"
        tooltip="Exportar a PDF"
        tooltipOptions={{ position: 'top' }}
        disabled={filasParaExportar.length === 0}
        onClick={manejarExportarPdf}
      />
    </div>
  );

  // Filtros
  const endToolbar = () => (
    <div className="tmc-toolbar-group">
      <Calendar
        value={periodo}
        onChange={(e) => setPeriodo(e.value)}
        selectionMode="range"
        placeholder="Filtrar por período"
        dateFormat="dd/mm/yy"
        showButtonBar
        className="w-auto"
      />

      <Dropdown
        value={filtroEstado}
        options={OPCIONES_ESTADO}
        onChange={(e) => setFiltroEstado(e.value)}
        placeholder="Estado"
        className="w-auto"
      />

      <Dropdown
        value={filtroMecanico}
        options={mecanicos}
        optionLabel="nombre"
        optionValue="id_trabajador"
        onChange={(e) => setFiltroMecanico(e.value)}
        placeholder="Mecánico"
        showClear
        className="w-auto"
      />

      <IconField iconPosition="left">
        <InputIcon className="pi pi-search pr-2 pt-1" />
        <InputText
          value={globalFilterValue}
          onChange={(e) => {
            const valor = e.target.value;
            setGlobalFilterValue(valor);
            setFilters({ global: { value: valor, matchMode: FilterMatchMode.CONTAINS } });
          }}
          placeholder="Buscar por..."
          maxLength={25}
          className='pl-6'
        />
      </IconField>
    </div>
  );

  const accionTemplate = (rowData) => {
    const cerrado = 
      rowData.estado_recepcion === 'entregado' &&
      rowData.estado_taller === 'completo' && 
      rowData.estado_firmas === 'completo';

    const puedeEntregar =
      rowData.estado_recepcion === 'ingresado' &&
      rowData.estado_taller === 'completo' &&
      rowData.estado_firmas === 'completo';

    return (
      <div className="accion-container">
        <Permiso modulo="Taller Mecánico" permiso="MEC_EDITAR_INFORME">
          <Button
            className="btn-editar"
            icon="pi pi-pencil"
            tooltip="Editar"
            tooltipOptions={{ position: 'top' }}
            disabled={cerrado}
            onClick={(e) => {
              e.stopPropagation();
              irARevision(rowData);
            }}
          />
        </Permiso>

        <Permiso modulo="Taller Mecánico" permiso="MEC_CERRAR_INFORME">
          <Button
            className="btn-outline-verde"
            icon="pi pi-check-circle"
            label="Entregar"
            disabled={!puedeEntregar}
            onClick={() => abrirConfirmacionEntrega(rowData)}
          />
        </Permiso>

        <Permiso modulo="Taller Mecánico" permiso="MEC_VER_DETINFORME">
          <Button
            className="btn-editar"
            icon="pi pi-eye"
            tooltip="Ver informe (PDF)"
            tooltipOptions={{ position: 'top' }}
            disabled={!cerrado}
            onClick={async (e) => {
              e.stopPropagation();

              // Abrir la ventana de forma síncrona, dentro del gesto del click
              // (si se abre después del await, iOS y otros navegadores la bloquean)
              const ventana = window.open('', '_blank');

              try {
                const blob = await revisionService.verPdfOficial(rowData.id_revision ?? rowData.id);
                const url = URL.createObjectURL(blob);

                if (ventana) {
                  ventana.location.href = url;
                } else {
                  toast.current?.show({
                    severity: 'warn',
                    summary: 'Popup bloqueado',
                    detail: 'Habilita las ventanas emergentes para ver el documento',
                    life: 4000,
                  });
                }
              } catch (err) {
                if (ventana) ventana.close();
                toast.current?.show({
                  severity: 'error',
                  summary: 'Error al abrir el PDF',
                  detail: typeof err === 'string' ? err : 'No se pudo obtener el documento',
                  life: 4000,
                });
              }
            }}
          />
        </Permiso>
      </div>
    );
  };

  return (
    <div className='p-2'>
      <TableLayout
        titulo="Seguimiento de Revisiones"
        logo={logo}
        onVolver={() => navigate('/app/taller')}
      >
        <Toast ref={toast} position="center" />

        <Toolbar 
          className="inv-toolbar gap-2" 
          start={startToolbar} 
          end={endToolbar}
        ></Toolbar>

        <EstaSeguro
          visible={mostrarConfirmacionEntrega}
          onHide={() => {
            if (entregandoVehiculo) return;
            setMostrarConfirmacionEntrega(false);
            setRevisionSeleccionada(null);
          }}
          onConfirm={confirmarEntregaVehiculo}
          titulo="¿Entregar el vehículo?"
          descripcion="¿Confirma que el vehículo fue entregado? El informe quedará en modo de solo lectura y se generará su versión oficial en PDF."
          icon="pi pi-car"
          confirmLabel="Sí, entregar"
          cancelLabel="Cancelar"
          loading={entregandoVehiculo}
        />

        <DataTable
          ref={dtRef}
          value={revisiones}
          loading={cargando}
          className="p-datatable-sm"
          style={{ cursor: 'pointer' }}
          rowHover
          stripedRows
          selectionMode="single"
  
          filters={filters}
          globalFilterFields={['patente', 'nombre_cliente', 'nombre', 'modelo', 'nombre_recepcionista']}
          
          paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} informes"
          emptyMessage="No hay revisiones que coincidan con el filtro."
        >
          <Column header="#" headerStyle={{ width: '1rem' }} body={(data, options) => options.rowIndex + 1}></Column>
          <Column field="fecha_ingreso"      header="Ingreso" body={(row) => formatFecha(row.fecha_ingreso, '/')} sortable align="center" />
          <Column field="numero_ot"          header="OT" sortable align="center" style={{ textAlign: 'center', justifyContent: 'center' }}/>
          <Column field="nombre_centrocosto" header="Centro Costo" sortable align="center" style={{ textAlign: 'center', justifyContent: 'center' }}/>
          <Column header="Recepciona"     body={recepcionistaTemplate} align="center"/>
          <Column header="Vehículo"       body={vehiculoTemplate} align="center" />
          <Column header="Cliente"        body={clienteTemplate} align="center"/>
          <Column header="Mecánicos"      body={mecanicosTemplate} align="center"/>
          <Column header="Recepción"      body={(row) => <BadgeEstadoRecepcion estado={row.estado_recepcion} />} align="center"/>
          <Column header="Taller"         body={(row) => <BadgeEstadoPaso estado={row.estado_taller} />} align="center"/>
          <Column header="Firmas"         body={(row) => <BadgeEstadoPaso estado={row.estado_firmas} />} align="center" />
          <Column field="dias_pendiente"  header="Días pendiente" body={diasTemplate} sortable align="center" />
          <Column
            header="Acción"
            align="center"
            style={{ textAlign: 'center', justifyContent: 'center' }}
            body={accionTemplate}
          />
        </DataTable>
      </TableLayout>
    </div>
  );
}

export default SeguimientoRevisiones;